import { get, isEmpty } from 'lodash-es'
import { formatDate } from '@common/utils'

import { AppState } from 'app-store'
import { BillingPeriodKeyMapper, TimePeriod } from '@common/constants'
import { getUserState, getLabelContent, getCheckoutState, getPlanData, getCouponState } from '@buy/state/selectors'
import { getCurrentAddons, getCurrentAgent, getCurrentEndpoint, getExtraAddons } from '@buy/utils/get-current-addons'
import { BuyPlan } from '@buy/state/plan'
import { buyCheckoutActions } from '@buy/state/checkout/actions'
import { getActiveCouponServerData, getSubscriptionInfo, ValidCoupon } from '@buy/utils'
import { ADDONS } from '@buy/constants'
import { getAddonVolumeDiscount } from '@buy/utils/calculate-volume-discount'
import { getAccountState, getMarketingState, getLocationState, getModalState } from '@common/state/selectors'
import { marketingActions } from '@common/state/marketing/actions'
import { modalActions } from '@common/state/modal/actions'
import { BuyAddon } from '@buy/state/add-on'
import { getContentLanguage } from '@buy/utils/language-helpers'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { BillingTenureValue } from '@buy/components/BillingPeriodSwitch'
import { buyCouponActions } from '@buy/state/coupon/actions'
import { BuyOrderSummaryState } from './state'

export const mapBuyOrderSummaryState = (state: AppState): BuyOrderSummaryState => {
    const content = getLabelContent(state)

    const { locationInfo } = getLocationState(state)
    const { locale: Locale, currency: CurrencyCode } = locationInfo

    const {
        selectedAddons,
        billingFrequency,
        calculatedTotal,
        calculatedTax,
        calculatedCredit,
        productPrices,
        calculatedNetTotal,
        totalLoading,
        currentVolumeDiscountPrice,
        selectedTierIndex,
        calculatedProratedAmount,
        endpoints,
        agents: totalAgents,
        baseLine,
        additionalLines,
        addonLines,
        totalDiscount,
        billingTenure,
        finalTotal,
    } = getCheckoutState(state)
    const language = getContentLanguage()
    const modal = getModalState(state)
    const planData: BuyPlan = getPlanData(state)
    const coupon = getCouponState(state)
    const {
        validCoupons,
        couponProcessedDataPerSku,
        isCouponValidAndApplied,
        couponDoubleDipAmountForNewUser,
        couponServerData,
    } = coupon

    const couponData = getActiveCouponServerData(coupon, planData)
    const couponDiscountType = couponData ? couponData.couponDiscountType : null
    const currSelectedAddons = getCurrentAddons(selectedAddons, planData)
    const currentTier = `tier${selectedTierIndex + 1}`
    const billingFrequencyKey = billingTenure.selected

    const baseTierAnnualPrice: number = get(planData, ['pricingInfo', CurrencyCode, 'annual', currentTier, 'price'], 0)
    const baseTierMonthlyPrice: number = get(
        planData,
        ['pricingInfo', CurrencyCode, 'monthly', currentTier, 'price'],
        0
    )
    const currentTierPrice: number = get(
        planData,
        ['pricingInfo', CurrencyCode, billingFrequencyKey, currentTier, 'price'],
        0
    )

    const {
        userType,
        existingAccountInformation,
        loggedIn: { isUserLoggedIn },
    } = getUserState(state)

    const { accountSubscriptions } = getAccountState(state)
    const { displayOptOut, isUserOptOut } = getMarketingState(state)
    const subscriptionInfo = getSubscriptionInfo(billingTenure.selected, planData, planData, accountSubscriptions)

    const planStartDate = formatDate(
        new Date(accountSubscriptions.find((sub) => sub.productSku === planData.sKU)?.startDate ?? Date.now()),
        'UTC',
        'en-US'
    )

    const extraAddonsSelected = getExtraAddons(currSelectedAddons)
    const currentAgent = getCurrentAgent(currSelectedAddons)
    const currentEndpoint = getCurrentEndpoint(currSelectedAddons)
    const disablePlanSelection = !subscriptionInfo.isReplacementBooking
    const disableExtraAddons = false
    const { includedAgents } = subscriptionInfo
    const { includedEndpoints } = subscriptionInfo
    const agentsAdded = (currentAgent && currentAgent.quantity > 0 ? currentAgent : null) as BuyAddon
    const endpointsAdded = (currentEndpoint && currentEndpoint.quantity > 0 ? currentEndpoint : null) as BuyAddon
    const agentContent = getCurrentAgent(planData.addons)

    const agentsPrice: number =
        agentsAdded && get(agentsAdded, ['pricingInfo', CurrencyCode, billingFrequencyKey, currentTier, 'price'], 0)
    const endpointsPrice: number =
        endpointsAdded &&
        get(endpointsAdded, ['pricingInfo', CurrencyCode, billingFrequencyKey, currentTier, 'price'], 0)

    let addonsVolumeDiscountTotal = 0

    if (agentsAdded) {
        const { discountPrice, tierIndex } = getAddonVolumeDiscount(
            agentsAdded,
            totalAgents - planData.userAgentsIncludedAmount,
            CurrencyCode,
            billingFrequencyKey,
            currentTier,
            totalAgents - includedAgents
        )
        agentsAdded.discountPrice = discountPrice
        agentsAdded.tier = `Tier${tierIndex}`
        addonsVolumeDiscountTotal += discountPrice
    }

    if (endpointsAdded) {
        const { discountPrice, tierIndex } = getAddonVolumeDiscount(
            endpointsAdded,
            (endpoints - planData.endpointsIncludedAmount) / ADDONS.ENDPOINTS_ADDONS_INTERVAL,
            CurrencyCode,
            billingFrequencyKey,
            currentTier,
            (endpoints - includedEndpoints) / ADDONS.ENDPOINTS_ADDONS_INTERVAL
        )
        endpointsAdded.discountPrice = discountPrice
        endpointsAdded.tier = `Tier${tierIndex}`
        addonsVolumeDiscountTotal += discountPrice
    }

    if (extraAddonsSelected.length) {
        // Swap the Mobile/Camera-Share Addons positions to show the "Mobile Addons" on top in case both the addons are selected and `Camera Share` addon is first selected
        if (extraAddonsSelected.length > 1 && extraAddonsSelected[0].sKU === ADDONS.CAMERA_SHARE_ADDON) {
            extraAddonsSelected.reverse()
        }

        if (extraAddonsSelected.length === 3) {
            const epp = extraAddonsSelected.filter((addon) => addon.sKU === ADDONS.ENDPOINT_PROTECTION_ADDON).pop()
            if (epp) {
                const endpointIndex = extraAddonsSelected.indexOf(epp)
                extraAddonsSelected.splice(endpointIndex, 1)
                extraAddonsSelected.splice(1, 0, epp)
            }
        }
        extraAddonsSelected.forEach((addon) => {
            /* eslint-disable no-param-reassign */
            const { discountPrice, tierIndex } = getAddonVolumeDiscount(
                addon,
                addon.sKU === ADDONS.ENDPOINT_PROTECTION_ADDON ? endpoints : totalAgents,
                CurrencyCode,
                billingFrequencyKey,
                currentTier,
                addon.quantity
            )
            addon.discountPrice = discountPrice
            addon.tier = `Tier${tierIndex}`
            addonsVolumeDiscountTotal += discountPrice
        })
    }
    const isCouponValid = coupon.isCouponValidAndApplied && coupon.couponServerData && !isEmpty(coupon.couponServerData)
    const couponCode = isCouponValid ? coupon.couponServerData?.couponCode ?? '' : ''
    const billingPeriodKeys = planData.billingPeriods.map((item) => {
        return BillingPeriodKeyMapper[item.key]
    })

    return {
        content: {
            ...content,
            proratedadjustmentTooltip: content.proratedadjustmentTooltip?.replace('{proratedDate}', planStartDate),
        },
        agentContent,
        modal,
        Locale,
        CurrencyCode,
        selectedAddons: currSelectedAddons,
        billingFrequency,
        selectedPlan: planData,
        calculatedTax,
        calculatedTotal,
        calculatedCredit,
        isUserLoggedIn,
        productPrices,
        calculatedNetTotal,
        calculatedProratedAmount,
        userType,
        existingAccountInformation,
        totalLoading,
        currentVolumeDiscountPrice,
        baseTierAnnualPrice,
        baseTierMonthlyPrice,
        currentTierPrice,
        couponProcessedDataPerSku,
        isCouponValidAndApplied,
        couponDiscountType,
        couponDoubleDipAmountForNewUser,
        agentsAdded,
        endpointsAdded,
        agentsPrice,
        endpointsPrice,
        currentTier,
        endpoints,
        extraAddonsSelected,
        addonsVolumeDiscountTotal,
        totalAgents,
        disablePlanSelection,
        disableExtraAddons,
        includedAgents,
        includedEndpoints,
        displayOptOut,
        isUserOptOut,
        subscriptionInfo,
        planData,
        couponCode,
        language,
        baseLine,
        additionalLines,
        addonLines,
        billingPeriodKeys,
        totalDiscount,
        billingTenure,
        finalTotal,
        couponServerData,
        validCoupons,
    }
}

export const mapBuyOrderSummaryActions = {
    setBillingFrequency: (frequency: TimePeriod) => buyCheckoutActions.setBillingFrequency(frequency),
    setIsUserOptOut: (isUserOutOut: boolean) => marketingActions.setIsUserOptOut(isUserOutOut),
    setDisplayOptOut: (displayOutOut: boolean) => marketingActions.setDisplayOptOut(displayOutOut),
    setBaseLine: (baseLine: OrderSummaryLine) => buyCheckoutActions.setBaseLine(baseLine),
    setAdditionalLines: (additionalLines: OrderSummaryLine[]) => buyCheckoutActions.setAdditionalLines(additionalLines),
    setAddonLines: (addonLine: OrderSummaryLine[]) => buyCheckoutActions.setAddonLines(addonLine),
    setTotalDiscount: (discount: number) => buyCheckoutActions.setTotalDiscount(discount),
    closeErrorModal: () => modalActions.setModalOpen(false),
    setBillingTenure: (tenure: BillingTenureValue) => buyCheckoutActions.setBillingTenure(tenure),
    setFinalTotal: (finalTotal: number) => buyCheckoutActions.setFinalTotal(finalTotal),
    setTotalVolumeDiscount: (volumeDiscount: number) => buyCheckoutActions.setTotalVolumeDiscount(volumeDiscount),
    setValidCoupons: (validCoupons: ValidCoupon[]) => buyCouponActions.setValidCoupons(validCoupons),
    setCouponValidAndApplied: (isValid: boolean) => buyCouponActions.setCouponValidAndApplied(isValid),
}

export type BuyOrderSummaryActionsType = typeof mapBuyOrderSummaryActions
